import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Form, Alert, Spinner } from 'react-bootstrap';
import { PlusCircleFill, QuestionCircleFill } from 'react-bootstrap-icons';
import { authHeaders } from '../../api';
import { ENDPOINTS } from '../../endpoints';
import { useFeedback } from '../../context/FeedbackContext';
import TicketModal from '../master/TicketModal';
import './SoporteView.css';

const STATUS_CLASS = {
  'Abierto':     'soporte-badge--abierto',
  'En progreso': 'soporte-badge--en-progreso',
  'Resuelto':    'soporte-badge--resuelto',
  'Cerrado':     'soporte-badge--cerrado',
};

const CANCEL_DISABLED = ['Cerrado', 'Resuelto'];

const SoporteView = () => {
  const feedback = useFeedback();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({ subject: '', description: '', category: 'Consulta' });
  const [createLoading, setCreateLoading] = useState(false);
  const [createError, setCreateError] = useState('');

  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [cancelLoading, setCancelLoading] = useState(false);

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showTicketModal, setShowTicketModal] = useState(false);

  const loadTickets = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(ENDPOINTS.TICKETS_MINE, { headers: authHeaders() });
      if (res.ok) setTickets(await res.json());
      else feedback.error('Error al cargar los tickets');
    } catch {
      feedback.error('Error al cargar los tickets');
    } finally {
      setLoading(false);
    }
  }, [feedback]);

  useEffect(() => { loadTickets(); }, [loadTickets]);

  const openCreateModal = () => {
    setCreateError('');
    setForm({ subject: '', description: '', category: 'Consulta' });
    setShowCreateModal(true);
  };

  const handleCreate = async () => {
    if (!form.subject.trim() || !form.description.trim()) {
      setCreateError('El asunto y la descripción son obligatorios');
      return;
    }
    setCreateLoading(true);
    setCreateError('');
    try {
      const res = await fetch(ENDPOINTS.TICKETS, {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al crear el ticket');
      const data = await res.json();
      setShowCreateModal(false);
      loadTickets();
      feedback.success(`Ticket creado exitosamente — N° ${data.ticket_id}`);
    } catch (e) {
      setCreateError(e.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancelConfirm = async () => {
    if (!cancelTarget) return;
    setCancelLoading(true);
    try {
      const res = await fetch(ENDPOINTS.TICKET_STATUS(cancelTarget.ticket_id), {
        method: 'PUT',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Cerrado' }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Error al cancelar el ticket');
      setShowCancelModal(false);
      setCancelTarget(null);
      loadTickets();
      feedback.success(`Ticket ${cancelTarget.ticket_number} cancelado`);
    } catch (e) {
      feedback.error(e.message);
      setShowCancelModal(false);
    } finally {
      setCancelLoading(false);
    }
  };

  return (
    <div className="global-section">

      {/* ── Encabezado ───────────────────────────────────────────────── */}
      <div className="soporte-header">
        <div>
          <h4 className="soporte-header-title">Centro de Soporte</h4>
          <p className="soporte-header-subtitle">
            Consulta el estado de tus reportes o levanta uno nuevo
          </p>
        </div>
        <button className="soporte-btn-nuevo" onClick={openCreateModal}>
          <PlusCircleFill size={16} />
          Nuevo Ticket
        </button>
      </div>

      {/* ── Cuerpo ───────────────────────────────────────────────────── */}
      <div className="soporte-content">

        {loading ? (
          <div className="soporte-loading">
            <Spinner animation="border" variant="primary" />
            <span>Cargando tickets...</span>
          </div>
        ) : tickets.length === 0 ? (
          <div className="soporte-empty">
            <div className="soporte-empty-icon">
              <QuestionCircleFill size={32} />
            </div>
            <p className="soporte-empty-title">Sin tickets por ahora</p>
            <p className="soporte-empty-text">
              Cuando tengas una consulta, error o sugerencia, puedes levantarlo aquí.
            </p>
            <button className="soporte-btn-nuevo" onClick={openCreateModal}>
              <PlusCircleFill size={16} />
              Crear primer ticket
            </button>
          </div>
        ) : (
          <div className="soporte-table-wrapper">
            <table className="soporte-table">
              <thead>
                <tr>
                  <th style={{ width: '140px' }}>N° Ticket</th>
                  <th>Asunto</th>
                  <th style={{ width: '110px' }}>Categoría</th>
                  <th style={{ width: '130px' }}>Estado</th>
                  <th style={{ width: '110px' }}>Fecha</th>
                  <th style={{ width: '150px' }}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {tickets.map(t => (
                  <tr key={t.ticket_id}>
                    <td>
                      <span className="soporte-ticket-number">{t.ticket_number}</span>
                    </td>
                    <td>
                      <span className="soporte-subject" title={t.subject}>{t.subject}</span>
                    </td>
                    <td className="soporte-category">{t.category}</td>
                    <td>
                      <span className={`soporte-badge ${STATUS_CLASS[t.status] || ''}`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="soporte-date">
                      {new Date(t.created_at).toLocaleDateString('es-CL')}
                    </td>
                    <td>
                      <div className="soporte-actions">
                        <button
                          className="soporte-btn-abrir"
                          onClick={() => { setSelectedTicket(t); setShowTicketModal(true); }}
                        >
                          Abrir
                        </button>
                        <button
                          className="soporte-btn-cancelar"
                          disabled={CANCEL_DISABLED.includes(t.status)}
                          onClick={() => { setCancelTarget(t); setShowCancelModal(true); }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal Nuevo Ticket ────────────────────────────────────────── */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton className="soporte-modal-header">
          <Modal.Title className="soporte-modal-title">Nuevo Ticket de Soporte</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {createError && (
            <Alert variant="danger" onClose={() => setCreateError('')} dismissible>
              {createError}
            </Alert>
          )}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Asunto <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                type="text"
                placeholder="Describe brevemente el problema o consulta"
                value={form.subject}
                onChange={e => setForm({ ...form, subject: e.target.value })}
                maxLength={200}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label className="fw-semibold">
                Categoría <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                value={form.category}
                onChange={e => setForm({ ...form, category: e.target.value })}
              >
                <option value="Consulta">Consulta</option>
                <option value="Error">Error</option>
                <option value="Mejora">Mejora</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-1">
              <Form.Label className="fw-semibold">
                Descripción <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={4}
                placeholder="Describe con detalle el problema, los pasos para reproducirlo, o tu consulta..."
                value={form.description}
                onChange={e => setForm({ ...form, description: e.target.value })}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer className="px-4 pb-4 pt-3 border-0">
          <button
            className="btn btn-light me-2"
            onClick={() => setShowCreateModal(false)}
          >
            Cancelar
          </button>
          <button
            className="soporte-btn-nuevo"
            onClick={handleCreate}
            disabled={createLoading || !form.subject.trim() || !form.description.trim()}
          >
            {createLoading
              ? <><Spinner size="sm" className="me-1" />Enviando...</>
              : <><PlusCircleFill size={15} />Enviar ticket</>
            }
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal Confirmar Cancelación ───────────────────────────────── */}
      <Modal show={showCancelModal} onHide={() => setShowCancelModal(false)} centered size="sm">
        <Modal.Header closeButton className="soporte-modal-header">
          <Modal.Title className="soporte-modal-title">Cancelar ticket</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          <p className="mb-0 text-neutral-700">
            ¿Confirmas que deseas cancelar el ticket{' '}
            <strong>{cancelTarget?.ticket_number}</strong>?{' '}
            Esta acción no se puede deshacer.
          </p>
        </Modal.Body>
        <Modal.Footer className="px-4 pb-4 pt-3 border-0">
          <button
            className="btn btn-light me-2"
            onClick={() => setShowCancelModal(false)}
          >
            Volver
          </button>
          <button
            className="btn btn-artemis btn-artemis-danger"
            onClick={handleCancelConfirm}
            disabled={cancelLoading}
            style={{ borderRadius: 'var(--border-radius-lg)' }}
          >
            {cancelLoading ? <Spinner size="sm" /> : 'Confirmar cancelación'}
          </button>
        </Modal.Footer>
      </Modal>

      {/* ── Modal detalle del ticket ──────────────────────────────────── */}
      <TicketModal
        show={showTicketModal}
        onHide={() => { setShowTicketModal(false); setSelectedTicket(null); }}
        ticket={selectedTicket}
        onUpdated={loadTickets}
      />
    </div>
  );
};

export default SoporteView;
